import { gql } from "@apollo/client";
import { alpha } from "@mui/material";
import { COLOR_WHITE } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import useData from "helpers/useData";
import useMobileLayout from "helpers/useMobileLayout";
import useSwipe from "helpers/useSwipe";
import React from "react";

import HighlightPlayer, { HighlightPlayerFragment } from "./HighlightPlayer";
import VideoPlayerDialog from "./VideoPlayerDialog";
import VideoPlayerDialogFooter from "./VideoPlayerDialogFooter";

// index, total, onPrevious and onNext are for stepping through a list of highlights; leave them out for a single one.
export default function HighlightPlayerDialog({ open, onClose, eventId, index, total, onPrevious, onNext }) {
  const [data] = useData(
    gql`
      query HighlightPlayerDialog($eventId: ID!) {
        event(id: $eventId) {
          id
          ...HighlightPlayerFragment
        }
      }
      ${HighlightPlayerFragment}
    `,
    { eventId },
    // eventId is briefly missing while the next page of a list loads
    { skip: !open || !eventId },
  );
  const mobileLayout = useMobileLayout();
  const navigable = !!(onPrevious || onNext);
  // like flicking through photos, a swipe to the left goes to the next highlight
  const swipeHandlers = useSwipe({ onSwipeLeft: onNext, onSwipeRight: onPrevious });

  return (
    <VideoPlayerDialog open={open} onClose={onClose} PaperProps={swipeHandlers}>
      {/* key: a fresh player for each highlight, so playback state doesn't carry over */}
      <HighlightPlayer
        key={eventId}
        event={data?.event}
        eventId={eventId}
        dragToSeek={!navigable}
        {...(mobileLayout
          ? {
              // one row on mobile like the Top 5 and reel dialogs, with swiping for previous and next
              compactHeader: true,
              counter: navigable && total > 0 && `${index + 1} of ${total}`,
              tip: navigable && <SwipeTip />,
            }
          : {
              // inside the player, so it stays right under the header when the video is centred on a tall screen
              footer: (
                <VideoPlayerDialogFooter
                  index={index}
                  total={total}
                  onPrevious={onPrevious}
                  onNext={onNext}
                  onClose={onClose}
                />
              ),
            })}
      />
    </VideoPlayerDialog>
  );
}

// Mobile has no previous and next buttons, so this tells people they can swipe
function SwipeTip() {
  return (
    <div
      style={{
        textAlign: "center",
        color: alpha(COLOR_WHITE, 0.6),
        fontFamily: FONT_FAMILY_GRAPHIK,
        fontSize: 13,
      }}
    >
      Swipe left for next, right for previous
    </div>
  );
}
